import axios from 'axios';
import { strictEqual } from 'assert';
import * as Api from '@/api';
import { v2ApiErrorHandler } from '@/api/lib/error-handler';
import { getv3ApiBase } from '@/api/index';
import { objectToCamelCase } from '@/services/utils';

export const listTagCategoriesForSchool = async ({
    schoolId,
    page = 1,
    page_size = 10,
    search = '',
    errorHandlerOptions = {}
} = {}) => {
    try {
        strictEqual(typeof schoolId, 'string');
        strictEqual(typeof page, 'number');
        strictEqual(typeof page_size, 'number');

        const url = Api.getv2ApiBase(`/tag-category`);
        const options = Api.getOptions();
        const params = {
            page,
            page_size,
            school_id: schoolId,
            // v2 and slim were introduced when transitioning from Django to Node.
            // So far the UI has only used `true` for both, so they're hardcoded here.
            v2: true,
            slim: true
        };

        if (typeof search === 'string' && search.length > 0) {
            params.search = search;
        }

        const response = await axios.get(url, { ...options, params });
        return response.data;
    } catch (error) {
        v2ApiErrorHandler({
            error,
            overrideMessage: 'There was a problem loading the Tag Categories. Please try again.',
            ...errorHandlerOptions
        });
    }
};

// V3 API - List tag categories with pagination support
export const listTagCategoriesV3 = async ({
    schoolId,
    page = 1,
    page_size = 10,
    search = '',
    is_public,
    ordering,
    errorHandlerOptions = {}
} = {}) => {
    try {
        strictEqual(typeof schoolId, 'string');
        strictEqual(typeof page, 'number');
        strictEqual(typeof page_size, 'number');

        const url = getv3ApiBase('/tag_categories/');
        const options = Api.getOptions();
        const params = {
            page,
            page_size,
            school: schoolId,
            is_public: false // is_public: true are retired categories
        };

        if (typeof search === 'string' && search.length > 0) {
            params.search = search;
        }

        if (typeof is_public === 'boolean') {
            params.is_public = is_public;
        }

        if (typeof ordering === 'string' && ordering.length > 0) {
            params.ordering = ordering;
        }

        const response = await axios.get(url, { ...options, params });

        // Transform snake_case fields to camelCase for UI compatibility
        const transformedData = objectToCamelCase(response.data);

        // Ensure all categories have a tagsRef array (some categories from v3 API don't have tags_ref field)
        if (transformedData.results) {
            transformedData.results = transformedData.results.map((category) => ({
                ...category,
                tagsRef: category.tagsRef || []
            }));
        }

        // TODO: Remove this once the backend is updated to provide next/previous URLs
        // Go backend has a TODO to add next/previous URLs to the response
        if (transformedData.count && !transformedData.next && !transformedData.previous) {
            const totalPages = Math.ceil(transformedData.count / page_size);

            if (page < totalPages) {
                transformedData.next = `page=${page + 1}`;
            }

            if (page > 1) {
                transformedData.previous = `page=${page - 1}`;
            }
        }

        return transformedData;
    } catch (error) {
        v2ApiErrorHandler({
            error,
            rethrow: true,
            overrideMessage: 'There was a problem loading the Tag Categories. Please try again.',
            ...errorHandlerOptions
        });
    }
};

// V3 API - Create a new tag category
export const createTagCategory = async ({ schoolId, tagCategory, errorHandlerOptions = {} }) => {
    try {
        strictEqual(typeof schoolId, 'string');
        strictEqual(typeof tagCategory, 'object');

        const url = getv3ApiBase('/tag_categories/');
        const options = Api.getOptions();
        const payload = {
            name: tagCategory.name,
            school: schoolId // Go backend expects 'school', not 'schoolId'
        };

        const response = await axios.post(url, payload, options);
        return response.data;
    } catch (error) {
        v2ApiErrorHandler({
            error,
            overrideMessage: 'There was a problem creating the Tag Category. Please try again.',
            ...errorHandlerOptions
        });
    }
};

// V3 API - Update an existing tag category
export const updateTagCategory = async ({
    id,
    schoolId,
    tagCategory,
    errorHandlerOptions = {}
}) => {
    try {
        strictEqual(typeof id, 'string');
        strictEqual(typeof schoolId, 'string');
        strictEqual(typeof tagCategory, 'object');

        const url = getv3ApiBase(`/tag_categories/${id}/`);
        const options = Api.getOptions();
        const payload = {
            name: tagCategory.name // Go backend only expects 'name' field
        };

        const response = await axios.patch(url, payload, options);
        return response.data;
    } catch (error) {
        v2ApiErrorHandler({
            error,
            overrideMessage: 'There was a problem updating the Tag Category. Please try again.',
            ...errorHandlerOptions
        });
    }
};

// V3 API - Delete a tag category
export const deleteTagCategory = async ({ id, errorHandlerOptions = {} }) => {
    try {
        strictEqual(typeof id, 'string');

        const url = getv3ApiBase(`/tag_categories/${id}/`);
        const options = Api.getOptions();
        // No params needed - school context determined by authentication

        const response = await axios.delete(url, options);
        return response.data;
    } catch (error) {
        v2ApiErrorHandler({
            error,
            overrideMessage: 'There was a problem deleting the Tag Category. Please try again.',
            ...errorHandlerOptions
        });
    }
};
